import { Try, toFailure, toSuccess } from '../types/commonTypes'
import { createAxios } from './axios'
import {
  CompareGamesResponse,
  DailyPlayersResponse,
  GetCompareGamesDataParams,
  GetOrgReportParams,
  GetPlayerReportParams,
  GetSponsoredReportParams,
  InstructorReportParams,
  InstructorReportResponse,
  OrgReportResponse,
  PlayerReportDataApiResponse,
  SponsoredReportApiGame,
  SponsoredReportSummaryResponse,
} from './reportingTypes'
import { TEST_USER_AUTHORIZATION } from './userApiService'

export const PAGE_SIZE = 20
export const SPONSORED_PAGE_SIZE = 5

export async function getOrgReportData({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  fromDate,
  untilDate,
  page = 1,
  perPage = PAGE_SIZE,
  sortBy = 'user_name asc',
}: GetOrgReportParams): Promise<Try<OrgReportResponse>> {
  try {
    const url = '/customer_reporting.json'
    const response = await createAxios(authorization).get<OrgReportResponse>(url, {
      params: {
        business_id: businessId,
        ...(fromDate && { gte_created: fromDate }),
        ...(untilDate && { lte_created: untilDate }),
        page,
        per_page: perPage,
        sort_column: sortBy,
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getOrgPlayersPerDay({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
}: Partial<GetOrgReportParams>): Promise<Try<DailyPlayersResponse>> {
  try {
    const url = '/players_per_days.json'
    const response = await createAxios(authorization).get<DailyPlayersResponse>(url, {
      params: {
        business_id: businessId,
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getCompareGamesData({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  gameIds,
}: GetCompareGamesDataParams): Promise<Try<CompareGamesResponse>> {
  try {
    const url = '/compare_games.json'
    const response = await createAxios(authorization).get<CompareGamesResponse>(url, {
      params: {
        business_id: businessId,
        game_ids: gameIds,
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getInstructorReportData({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  fromDate,
  untilDate,
  page = 1,
  perPage = PAGE_SIZE,
  sortBy = 'name asc',
}: InstructorReportParams): Promise<Try<InstructorReportResponse>> {
  try {
    const url = '/teacher_reporting.json'
    const response = await createAxios(authorization).get<InstructorReportResponse>(url, {
      params: {
        business_id: businessId,
        ...(fromDate && { gte_created: fromDate }),
        ...(untilDate && { lte_created: untilDate }),
        page,
        per_page: perPage,
        sort_column: sortBy,
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getSponsoredReportData({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  fromDate,
  untilDate,
}: GetSponsoredReportParams): Promise<Try<SponsoredReportSummaryResponse>> {
  try {
    const url = '/sponsored_games_reporting.json'
    const response = await createAxios(authorization).get<SponsoredReportSummaryResponse>(url, {
      params: {
        business_id: businessId,
        ...(fromDate && { gte_created: fromDate }),
        ...(untilDate && { lte_created: untilDate }),
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getSponsoredReportGameData({
  authorization = TEST_USER_AUTHORIZATION,
  gameId,
  businessId,
  page,
  perPage,
  fromDate,
  untilDate,
  sortBy = 'created_at desc',
}: GetSponsoredReportParams): Promise<Try<SponsoredReportApiGame>> {
  try {
    const url = `/sponsored_game_contents/${gameId}.json`
    const response = await createAxios(authorization).get<SponsoredReportApiGame>(url, {
      params: {
        business_id: businessId,
        id: gameId,
        page,
        per_page: perPage,
        ...(fromDate && { gte_created: fromDate }),
        ...(untilDate && { lte_created: untilDate }),
        sort_column: sortBy,
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getSponsoredPlayersPerDay({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  gameId,
}: Partial<GetSponsoredReportParams>): Promise<Try<DailyPlayersResponse>> {
  try {
    const url = '/sponsored_game_players_per_days.json'
    const response = await createAxios(authorization).get<DailyPlayersResponse>(url, {
      params: {
        business_id: businessId,
        conlib_game_id: gameId,
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getPlayerReportData({
  authorization = TEST_USER_AUTHORIZATION,
  fromDate,
  untilDate,
  gameName,
  playerEmail,
  tag,
}: GetPlayerReportParams): Promise<Try<PlayerReportDataApiResponse>> {
  try {
    const url = '/player_reporting_ux3.json'
    const response = await createAxios(authorization).get<PlayerReportDataApiResponse>(url, {
      params: {
        ...(fromDate && { gte_created: fromDate }),
        ...(untilDate && { lte_created: untilDate }),
        ...(gameName && { game_name: gameName }),
        ...(playerEmail && { email: playerEmail }),
        ...(tag && { tag }),
      },
    })

    const { data } = response

    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}
